import { useState, useEffect, useCallback } from 'react';
import { api } from '../services/api';
import { PublicationType } from './usePublications';
import { logger } from '../utils/logger';

export interface Subscription {
  id: string;
  customer_id: string;
  tenant_id: string;
  push_enabled: boolean;
  email_enabled: boolean;
  sms_enabled: boolean;
  preferred_types?: PublicationType[];
  preferences?: {
    types?: PublicationType[];
    categories?: string[];
  };
  created_at: string;
  updated_at: string;
}

export interface SubscriptionStats {
  total: number;
  push: number;
  email: number;
  sms: number;
}

export interface UseSubscriptionReturn {
  subscription: Subscription | null;
  loading: boolean;
  error: string | null;
  isSubscribed: boolean;
  subscribe: (data?: Partial<SubscribeData>) => Promise<void>;
  unsubscribe: () => Promise<void>;
  updatePreferences: (preferences: UpdatePreferencesData) => Promise<void>;
  refresh: () => Promise<void>;
}

export interface SubscribeData {
  push_enabled?: boolean;
  email_enabled?: boolean;
  sms_enabled?: boolean;
  preferred_types?: PublicationType[];
  preferred_categories?: string[];
}

export interface UpdatePreferencesData extends SubscribeData {}

export const useSubscription = (
  tenantId: string,
  customerId?: string
): UseSubscriptionReturn => {
  // Debug logs removed to avoid console pollution

  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [loading, setLoading] = useState(true); // Start with true while fetching
  const [error, setError] = useState<string | null>(null);

  const fetchSubscription = useCallback(async () => {
    if (!customerId || !tenantId) {
      logger.log('useSubscription - No customerId or tenantId, setting subscription to null');
      setSubscription(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      logger.log('useSubscription - Fetching subscription for customer:', customerId);
      const subscriptionData = await api.get(`/subscriptions/${customerId}`, {
        headers: { 'X-Tenant-ID': tenantId },
      });

      logger.log('useSubscription - Subscription found:', subscriptionData);
      setSubscription(subscriptionData);
    } catch (err: any) {
      if (err.response?.status === 404) {
        // Pas encore abonné
        logger.log('useSubscription - Subscription not found (404), user not subscribed');
        setSubscription(null);
      } else {
        setError(err.message || 'Erreur lors du chargement de l\'abonnement');
        logger.error('useSubscription - Error fetching subscription:', err);
        setSubscription(null);
      }
    } finally {
      setLoading(false);
    }
  }, [tenantId, customerId]);

  const subscribe = useCallback(async (data: Partial<SubscribeData> = {}) => {
    logger.log('useSubscription - subscribe called with data:', data);
    if (!customerId || !tenantId) {
      logger.error('useSubscription - subscribe: Missing customerId or tenantId');
      throw new Error('Customer ID et Tenant ID requis');
    }

    try {
      setLoading(true);
      setError(null);

      logger.log('useSubscription - subscribe: Calling API with:', {
        customer_id: customerId,
        push_enabled: data.push_enabled ?? true,
        email_enabled: data.email_enabled ?? false,
        sms_enabled: data.sms_enabled ?? false,
        preferred_types: data.preferred_types || [],
        preferred_categories: data.preferred_categories || [],
      });

      const subscriptionData = await api.post(
        '/subscriptions',
        {
          customer_id: customerId,
          push_enabled: data.push_enabled ?? true,
          email_enabled: data.email_enabled ?? false,
          sms_enabled: data.sms_enabled ?? false,
          preferred_types: data.preferred_types || [],
          preferred_categories: data.preferred_categories || [],
        },
        {
          headers: { 'X-Tenant-ID': tenantId },
        }
      );

      logger.log('useSubscription - subscribe: API response:', subscriptionData);
      setSubscription(subscriptionData);
      logger.log('useSubscription - subscribe: Subscription state updated');
    } catch (err: any) {
      logger.error('useSubscription - subscribe: Error:', err);
      setError(err.response?.data?.message || 'Erreur lors de l\'abonnement');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [tenantId, customerId]);

  const unsubscribe = useCallback(async () => {
    if (!customerId || !tenantId) {
      throw new Error('Customer ID et Tenant ID requis');
    }

    try {
      setLoading(true);
      setError(null);

      await api.delete(`/subscriptions/${customerId}`, {
        headers: { 'X-Tenant-ID': tenantId },
      });

      setSubscription(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erreur lors du désabonnement');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [tenantId, customerId]);

  const updatePreferences = useCallback(async (preferences: UpdatePreferencesData) => {
    if (!customerId || !tenantId) {
      throw new Error('Customer ID et Tenant ID requis');
    }

    try {
      setLoading(true);
      setError(null);

      const subscriptionData = await api.put(
        `/subscriptions/${customerId}`,
        preferences,
        {
          headers: { 'X-Tenant-ID': tenantId },
        }
      );

      setSubscription(subscriptionData);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erreur lors de la mise à jour');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [tenantId, customerId]);

  const refresh = useCallback(async () => {
    await fetchSubscription();
  }, [fetchSubscription]);

  useEffect(() => {
    fetchSubscription();
  }, [fetchSubscription]);

  return {
    subscription,
    loading,
    error,
    isSubscribed: !!subscription && subscription !== null,
    subscribe,
    unsubscribe,
    updatePreferences,
    refresh,
  };
};

// Hook pour les statistiques d'abonnements (côté admin)
export const useSubscriptionStats = (tenantId: string) => {
  const [stats, setStats] = useState<SubscriptionStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchStats = useCallback(async () => {
    if (!tenantId) return;

    try {
      setLoading(true);
      setError(null);

      const statsData = await api.get('/subscriptions/stats/count', {
        headers: { 'X-Tenant-ID': tenantId },
      });

      setStats(statsData);
    } catch (err: any) {
      setError(err.message || 'Erreur lors du chargement des statistiques');
      logger.error('Error fetching subscription stats:', err);
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  return { stats, loading, error, refresh: fetchStats };
};
